import React, { useEffect, useMemo, useState } from "react";
import { fmtTime, fmtRange } from "@/lib/enrol";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { X } from "lucide-react";
import EmailOptInCheckbox, { RequiredMark } from "@/components/EmailOptInCheckbox";
import PhotoConsentCheckbox from "@/components/PhotoConsentCheckbox";

export default function WalkInModal({ onClose, onDone }) {
  const [sessions, setSessions] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", notes: "",
    amount: "", payment_status: "paid", email_opt_in: false, photo_consent: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/sessions");
        const list = (r.data || []).slice().sort((a, b) => {
          const ka = `${a.day || ""}${a.start_time || ""}`;
          const kb = `${b.day || ""}${b.start_time || ""}`;
          return ka.localeCompare(kb);
        });
        setSessions(list);
      } catch { setSessions([]); }
    })();
  }, []);

  const selected = useMemo(() => (sessions || []).find(s => s.id === sessionId), [sessions, sessionId]);
  useEffect(() => {
    if (selected && form.amount === "") {
      const p = Number(selected.price ?? 0);
      if (p > 0) setForm(f => ({ ...f, amount: String(p) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const submit = async () => {
    if (!sessionId) { toast.error("Pick a class"); return; }
    if (!form.full_name.trim()) { toast.error("Attendee name is required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/sessions/${sessionId}/manual-register`, {
        full_name: form.full_name, email: form.email, phone: form.phone, notes: form.notes,
        amount: form.amount === "" ? 0 : Number(form.amount),
        payment_status: form.payment_status,
        email_opt_in: form.email_opt_in,
        photo_consent: form.photo_consent,
      });
      toast.success(`${form.full_name} registered`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 overflow-y-auto" onClick={onClose}>
      <div className="card-surface p-6 max-w-lg w-full my-8" onClick={e => e.stopPropagation()} data-testid="walkin-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-2xl uppercase">Register walk-in</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="eyebrow block mb-1">Class<RequiredMark/></label>
            <select value={sessionId} onChange={e => setSessionId(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-session">
              <option value="">— Pick a class —</option>
              {(sessions || []).map(s => (
                <option key={s.id} value={s.id}>
                  {s.batch_name || "Class"} · {s.day || "—"} {fmtTime(s.start_time)}{s.location_name ? ` · ${s.location_name}` : ""}{s.price ? ` · $${Number(s.price).toFixed(2)}` : ""}
                </option>
              ))}
            </select>
            {sessions === null && <div className="text-zinc-500 text-[11px] mt-1">Loading classes…</div>}
          </div>
          <div>
            <label className="eyebrow block mb-1">Full name<RequiredMark/></label>
            <input placeholder="Full name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-name"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="eyebrow block mb-1">Email</label>
              <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-email"/>
            </div>
            <div>
              <label className="eyebrow block mb-1">Phone</label>
              <input type="tel" placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-phone"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="eyebrow block mb-1">Amount paid ($)</label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-amount"/>
            </div>
            <div>
              <label className="eyebrow block mb-1">Payment status</label>
              <select value={form.payment_status} onChange={e => setForm({...form, payment_status: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-status">
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="manual">Manual</option>
                <option value="comp">Comp / free</option>
              </select>
            </div>
          </div>
          <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="walkin-notes"/>
          <EmailOptInCheckbox checked={form.email_opt_in} onChange={v => setForm({...form, email_opt_in: v})} testId="walkin-email-opt-in"/>
          <PhotoConsentCheckbox checked={form.photo_consent} onChange={v => setForm({...form, photo_consent: v})} testId="walkin-photo-consent"/>
        </div>
        <button onClick={submit} disabled={saving || !form.full_name.trim() || !sessionId || !form.photo_consent} className="btn-red w-full mt-4" data-testid="walkin-submit">{saving ? "Adding…" : "Register attendee"}</button>
      </div>
    </div>
  );
}
